<template lang="pug">
.option-target-family-list-modal
  // modal
  b-modal(v-model="_value" size="xl" @ok="ok" no-close-on-backdrop no-close-on-esc)
    template(#modal-header="{ ok }")
      .option-target-family-list-modal-header
        .d-flex.justify-content-start.align-items-center
          .d-flex.justify-content-start
            span.menu-title {{ _title }}
          .d-flex.justify-content-end.flex-fill(v-if="_showPdfDownloadBtn == true")
            button.btn(@click="onPdfDownload" type="button") PDFダウンロード
      button.close(type="button" @click="ok") ×

    .option-target-family-list-modal-body
      OptionTargetFamilyTable(
        v-model="_items"
        :fields="fields"
        :isLoading="isLoading"
      )

    template(#modal-footer="{ ok }")
      b-button.btn.modal-ok-btn(@click="ok") 閉じる
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";

import { userStore, fontsStore } from "@/store/store";

import { SendDocumentsOptionTargetFamiliesPdf } from "@/commons/utils/SendDocumentsOptionTargetFamiliesPdf";

import OptionTargetFamilyTable, { OptionTargetFamilyTableField, OptionTargetFamilyTableItem } from "@/pc/containers/sendDocuments/OptionTargetFamilyTable.vue";
import { SendDocumentGetModel } from "@/commons/services/SendDocuments";
import { FacilityGetModel } from "@/commons/services/Facilities";

/**
 * ### 文書送付、確認・同意ユーザー一覧モーダル
 */
@Component({
  components: {
    OptionTargetFamilyTable
  }
})
export default class OptionTargetFamilyListModal extends Vue {
  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;
  /** テーブル行情報 */
  @Prop({ required: true })
  items!: Array<OptionTargetFamilyTableItem>;
  /** 読込み中か否か */
  @Prop({ required: true })
  isLoading!: boolean;
  /** モーダル種別(同意か確認か) */
  @Prop()
  modalType!: OptionTargetFamilyListModalType;
  /** PDFで使用する文書送付 */
  @Prop()
  sendDocument!: SendDocumentGetModel;

  /** テーブル列情報 */
  fields: Array<OptionTargetFamilyTableField> = [];

  /** 施設名 */
  @Getter("getFacility") facility!: FacilityGetModel;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _items(): Array<OptionTargetFamilyTableItem> {
    return this.items;
  }

  set _items(newValue: Array<OptionTargetFamilyTableItem>) {
    this.$emit("update:items", newValue);
    return;
  }

  get _title(): string {
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.AGREEMENT: {
        return "同意済一覧";
      }
      case OptionTargetFamilyListModalType.NON_AGREEMENT: {
        return "未同意一覧";
      }
      case OptionTargetFamilyListModalType.CONFIRM: {
        return "確認済一覧";
      }
      case OptionTargetFamilyListModalType.NON_CONFIRM: {
        return "未確認一覧";
      }
      default: {
        return "";
      }
    }
  }

  get _showPdfDownloadBtn(): boolean {
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.AGREEMENT:
      case OptionTargetFamilyListModalType.CONFIRM: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  get _sendDocumentDatetime(): string {
    if (this.sendDocument == null) {
      return "";
    }

    const datetime: string = this.$dateUtil.convertNumberToString(this.sendDocument.sendDatetime as number, "yyyy年 mm月 dd日 hh:MM", false);

    return datetime;
  }

  /**
   * ### vue lifesycle mounted
   */
  public mounted(): void {
    let fields: Array<OptionTargetFamilyTableField> = this.createFields();
    this.fields = fields;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<OptionTargetFamilyTableField> {
    let fields: Array<OptionTargetFamilyTableField> = [];

    fields.push({ key: "groupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "group-name" });
    fields.push({ key: "residentName", label: "利用者名（部屋名）", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "familyName", label: "家族名（続柄）", sortable: false, sortDirection: "desc", class: "family-name" });
    fields.push({ key: "familyTel", label: "電話番号", sortable: false, sortDirection: "desc", class: "family-tel" });
    fields.push({ key: "datetime", label: "返信日時", sortable: false, sortDirection: "desc", class: "datetime" });
    fields.push({ key: "message", label: "返信時の連絡事項", sortable: false, sortDirection: "desc", class: "message" });

    return fields;
  }

  public ok(): void {
    this.$emit("ok");
    return;
  }

  /**
   * ### PDFダウンロードボタン押下イベント
   */
  public async onPdfDownload(): Promise<void> {
    this.createAndDownloadPdf();
    return;
  }

  /**
   * ### PDFを作成する
   */
  public async createAndDownloadPdf(): Promise<void> {
    const fileName: string = this.createFileName();
    const font: string = await fontsStore.getPdfFont({ userStore });
    const sendDocumentsReportPdfUtil: SendDocumentsOptionTargetFamiliesPdf = new SendDocumentsOptionTargetFamiliesPdf(font);
    await sendDocumentsReportPdfUtil.createAndDownloadPdf(this._items, this.facility.name, this.sendDocument.title, this._sendDocumentDatetime, this.modalType, fileName);

    return;
  }

  /**
   * ### PDFのファイル名を作成する
   */
  public createFileName(): string {
    let fileName: string = "";
    switch (this.modalType) {
      case OptionTargetFamilyListModalType.AGREEMENT: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-同意済一覧.pdf`;
        break;
      }
      case OptionTargetFamilyListModalType.CONFIRM: {
        fileName = `HitomeQコネクト-文書送付-${this.sendDocument.title}-確認済一覧.pdf`;
        break;
      }
      default: {
        fileName = "";
        break;
      }
    }

    return fileName;
  }
}

export enum OptionTargetFamilyListModalType {
  UNKNOWN = -1,
  AGREEMENT = 0,
  NON_AGREEMENT = 1,
  CONFIRM = 2,
  NON_CONFIRM = 3
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.option-target-family-list-modal {
  .pdf-contetns {
    // A4用紙相当のサイズを取る
    width: 159mm;
    height: 209mm;
    padding: 10mm;

    .datetime {
      font-size: 10px;
    }
    .title {
      font-size: 18px;
      font-weight: bold;
    }
    .send-document-infos {
      font-size: 10px;
    }

    .contents {
      font-size: 10px;
      margin-left: auto;
      margin-right: auto;

      table {
        width: 100%;
        border: 1px solid $border-darkgray;

        thead {
          background-color: $bg-header;
          text-align: center;
        }
        th,
        td {
          padding: 0 0 4px 4px;
          border: 1px solid $border-darkgray;
        }
        .resident-group,
        .resident-name,
        .family-name {
          width: 220px;
        }
        .reply-datetime {
          width: 200px;
        }
      }
      margin-bottom: 40px;
    }
  }
}

.pageout {
  position: fixed;
  z-index: -100;
}
</style>
